import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import AdminLayout from '../../layouts/AdminLayout'
import { useAuthStore } from '@store'
import apiClient from '@services/api/client'

const AdminRolesPage = () => {
  const storeUser = useAuthStore(state => state.user)
  const [currentUser, setCurrentUser] = useState(null)
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'person',
    color: '#6b7280'
  })

  // Options d'icônes Material Icons
  const iconOptions = [
    // Rôles & Utilisateurs
    'person', 'people', 'group', 'supervisor_account', 'admin_panel_settings',
    'manage_accounts', 'account_circle', 'badge', 'contacts', 'recent_actors',
    'switch_account', 'engineering', 'support_agent', 'health_and_safety',
    'person_add', 'group_add', 'face', 'sentiment_satisfied_alt', 'account_box',
    
    // Sécurité & Protection
    'security', 'shield', 'verified_user', 'lock', 'vpn_key', 'key',
    'password', 'fingerprint', 'gavel', 'policy', 'privacy_tip',
    'admin_panel_settings', 'shield_person', 'lock_person', 'enhanced_encryption',
    
    // Gestion & Organisation
    'business_center', 'work', 'corporate_fare', 'domain', 'apartment',
    'store', 'storefront', 'local_library', 'school', 'business',
    'work_outline', 'local_offer', 'room_service', 'store_mall_directory',
    
    // Actions & Permissions
    'check_circle', 'verified', 'rule', 'task_alt', 'assignment_turned_in',
    'done_all', 'how_to_reg', 'fact_check', 'checklist', 'grading',
    'assignment', 'assignment_ind', 'assignment_late', 'published_with_changes',
    
    // Communication & Support
    'support', 'headset_mic', 'contact_support', 'help', 'info',
    'campaign', 'announcement', 'notifications_active', 'notifications',
    'phone', 'email', 'chat', 'forum', 'record_voice_over',
    
    // Technique & Outils
    'settings', 'build', 'construction', 'handyman', 'hardware',
    'precision_manufacturing', 'developer_mode', 'code', 'terminal',
    'computer', 'laptop', 'desktop_windows', 'devices', 'phone_android',
    'memory', 'router', 'power', 'cable', 'usb',
    
    // Leadership & Direction
    'stars', 'star', 'military_tech', 'emoji_events', 'workspace_premium',
    'grade', 'new_releases', 'whatshot', 'trending_up', 'insights',
    'rocket_launch', 'flag', 'local_fire_department', 'bolt', 'flash_on',
    'diamond', 'auto_awesome', 'celebration',
    
    // Métiers & Professions
    'local_shipping', 'local_taxi', 'local_hospital', 'restaurant',
    'hotel', 'flight', 'directions_car', 'train', 'agriculture',
    'science', 'biotech', 'psychology', 'theater_comedy', 'sports_esports',
    
    // Social & Communauté
    'favorite', 'thumb_up', 'share', 'volunteer_activism', 'diversity_3',
    'celebration', 'mood', 'psychology', 'self_improvement', 'spa'
  ]

  // Options de couleurs
  const colorOptions = [
    { name: 'Violet', value: '#9333ea' },
    { name: 'Bleu', value: '#2563eb' },
    { name: 'Vert', value: '#16a34a' },
    { name: 'Orange', value: '#ea580c' },
    { name: 'Jaune', value: '#ca8a04' },
    { name: 'Rouge', value: '#dc2626' },
    { name: 'Rose', value: '#db2777' },
    { name: 'Gris', value: '#6b7280' },
    { name: 'Indigo', value: '#4f46e5' },
    { name: 'Cyan', value: '#0891b2' }
  ]

  // Charger les rôles
  const loadRoles = async () => {
    try {
      setLoading(true)
      const result = await apiClient.get('/roles')
      setRoles(result.data || result.roles || [])
    } catch (error) {
      console.error('Erreur:', error)
      toast.error('Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    console.log('=== INITIALISATION PAGE ROLES ===')
    console.log('Store user au démarrage:', storeUser)
    
    const initializePage = async () => {
      let userToUse = storeUser
      
      // Si l'utilisateur n'est pas chargé, récupérer depuis le store persisté
      if (!userToUse) {
        console.log('User est null, tentative de récupération depuis storage...')
        
        // Le authStore utilise 'auth-storage' comme clé
        const rememberMe = localStorage.getItem('rememberMe')
        const storage = rememberMe === 'true' ? localStorage : sessionStorage
        const authData = storage.getItem('auth-storage')
        
        console.log('rememberMe:', rememberMe)
        console.log('Storage utilisé:', rememberMe === 'true' ? 'localStorage' : 'sessionStorage')
        console.log('auth-storage trouvé:', authData)
        
        if (authData) {
          try {
            const parsed = JSON.parse(authData)
            console.log('Auth data parsé:', parsed)
            
            if (parsed.state?.user) {
              console.log('User trouvé dans storage:', parsed.state.user)
              userToUse = parsed.state.user
              useAuthStore.getState().setUser(userToUse)
            } else {
              console.log('Pas de user dans auth-storage')
            }
          } catch (error) {
            console.error('Erreur parsing auth-storage:', error)
          }
        } else {
          console.log('Aucune donnée auth-storage trouvée')
        }
      } else {
        console.log('User déjà chargé:', storeUser)
      }
      
      // Mettre à jour l'état local avec l'utilisateur
      setCurrentUser(userToUse)
      console.log('CurrentUser défini à:', userToUse)
      
      await loadRoles()
      
      // Debug après chargement
      console.log('Can manage roles:', userToUse?.role_id, [1,2,6].includes(userToUse?.role_id))
      console.log('Is super admin:', userToUse?.role_id === 1)
    }
    
    initializePage()
  }, [storeUser])

  // Tous les utilisateurs peuvent gérer les rôles
  const isSuperAdmin = () => {
    return true
  }

  // Tous les utilisateurs peuvent gérer les rôles
  const canManageRoles = () => {
    return true
  }

  // Ouvrir le modal pour créer/éditer
  const openModal = (role = null) => {
    
    if (role) {
      setEditingRole(role)
      setFormData({
        name: role.name,
        description: role.description || '',
        icon: role.icon || 'person',
        color: role.color || '#6b7280'
      })
    } else {
      setEditingRole(null)
      setFormData({
        name: '',
        description: '',
        icon: 'person',
        color: '#6b7280'
      })
    }
    setShowModal(true)
  }

  // Fermer le modal
  const closeModal = () => {
    setShowModal(false)
    setEditingRole(null)
    setFormData({ name: '', description: '', icon: 'person', color: '#6b7280' })
  }

  // Sauvegarder un rôle
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error('Le nom du rôle est requis')
      return
    }

    try {
      const body = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        icon: formData.icon,
        color: formData.color
      }

      if (editingRole) {
        await apiClient.put('/roles/' + editingRole.id, body)
        toast.success('✅ Rôle modifié avec succès')
      } else {
        await apiClient.post('/roles', body)
        toast.success('✅ Rôle créé avec succès')
      }

      closeModal()
      loadRoles()
    } catch (error) {
      console.error('Erreur:', error)
      toast.error('Erreur de connexion')
    }
  }

  // Supprimer un rôle
  const handleDelete = async (role) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le rôle "${role.name}" ?\n\nAttention : Tous les utilisateurs ayant ce rôle seront affectés.`)) {
      return
    }

    try {
      await apiClient.delete('/roles/' + role.id)
      toast.success('✅ Rôle supprimé avec succès')
      loadRoles()
    } catch (error) {
      console.error('Erreur:', error)
      toast.error('Erreur de connexion')
    }
  }

  // Couleurs selon le rôle
  const getRoleColor = (roleId) => {
    switch(roleId) {
      case 1: return 'bg-gradient-to-r from-purple-600 to-purple-700 text-white'
      case 2: return 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
      case 3: return 'bg-gradient-to-r from-green-600 to-green-700 text-white'
      case 4: return 'bg-gradient-to-r from-orange-600 to-orange-700 text-white'
      case 6: return 'bg-gradient-to-r from-yellow-600 to-yellow-700 text-white'
      case 14: return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
      default: return 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white'
    }
  }

  // Vérifier si un rôle est un rôle système
  const isSystemRole = (roleId) => {
    return [1, 2, 3, 4, 6, 14].includes(roleId)
  }

  return (
    <AdminLayout>
      <div className="bg-gray-50 space-y-6 mt-10">
        <div className="w-full mx-auto">
          
          {/* Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
            <div className="p-6 flex items-center justify-between border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] rounded-2xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-800">Gestion des Rôles</h1>
                  <p className="text-gray-500 mt-1">Gérez et organisez les rôles du système</p>
                </div>
              </div>
              {/* <div className="text-right">
                <p className="text-4xl font-bold text-[#D4AF37]">{roles.length}</p>
                <p className="text-gray-500 text-sm">Rôles actifs</p>
              </div> */}

              {/* Barre d'action */}
              <div className="p-6">
                <div className="flex items-center justify-end">
                  {canManageRoles() && (
                    <button
                      onClick={() => openModal()}
                      className="px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#c09d2a] text-white rounded-lg hover:shadow-lg transition-all font-medium flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      Nouveau Rôle
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Stats - Cartes par rôle */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
            {roles.map((role) => (
              <div key={role.id} className="card-ligdi hover:shadow-xl transition-all">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center shadow-md"
                    style={{ backgroundColor: role.color || '#6b7280' }}
                  >
                    <span className="material-icons text-white" style={{ fontSize: '28px' }}>
                      {role.icon || 'person'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-600 text-xs font-medium">{role.name}</p>
                    <p className="text-2xl font-bold text-gray-800">{role.users_count || 0}</p>
                    <p className="text-xs text-gray-500">utilisateur(s)</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Liste des rôles */}
          <div className="card-ligdi">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <svg className="w-6 h-6 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                Liste des Rôles
              </h2>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="animate-pulse bg-gray-100 rounded-xl p-6 h-24"></div>
                ))}
              </div>
            ) : roles.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-20 h-20 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <p className="text-gray-500 font-medium">Aucun rôle trouvé</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {roles.map((role) => (
                  <div
                    key={role.id}
                    className="border-2 border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all bg-gradient-to-br from-white to-gray-50"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                          style={{ backgroundColor: role.color || '#6b7280' }}
                        >
                          <span className="material-icons text-white" style={{ fontSize: '24px' }}>
                            {role.icon || 'person'}
                          </span>
                        </div>
                        <div className={`px-4 py-2 rounded-lg text-sm font-bold shadow-md ${getRoleColor(role.id)}`}>
                          {role.name}
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                        ID: {role.id}
                      </span>
                    </div>
                    
                    {role.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {role.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        {role.users_count || 0} utilisateur(s)
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {canManageRoles() ? (
                          <>
                            <button
                              onClick={() => openModal(role)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            {isSuperAdmin() && (
                              <button
                                onClick={() => handleDelete(role)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Supprimer"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded italic">Gestion réservée</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Créer/Éditer */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-8 py-6 bg-gradient-to-r from-[#D4AF37] to-[#c09d2a] rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                {editingRole ? 'Modifier le rôle' : 'Nouveau rôle'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-white/20 rounded-xl transition-colors">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-8 space-y-6 overflow-y-auto flex-1">
              <div>
                <label className="flex text-sm font-bold text-gray-800 mb-3 items-center gap-2">
                  <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Nom du rôle
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Modérateur, Gestionnaire..."
                  className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="flex text-sm font-bold text-gray-800 mb-3 items-center gap-2">
                  <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                  Description (optionnelle)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Décrivez les responsabilités de ce rôle..."
                  className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition-all resize-none shadow-sm"
                  rows="4"
                />
              </div>

              {/* Icône */}
              <div>
                <label className="flex text-sm font-bold text-gray-800 mb-3 items-center gap-2">
                  <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  Icône
                </label>
                <div className="grid grid-cols-10 gap-2 mb-3 max-h-48 overflow-y-auto p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200">
                  {iconOptions.map((icon, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setFormData({...formData, icon: icon})}
                      className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all hover:scale-110 hover:shadow-md ${
                        formData.icon === icon 
                          ? 'bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] ring-2 ring-[#001F3F] shadow-lg scale-105' 
                          : 'bg-white hover:bg-gray-50 border border-gray-200'
                      }`}
                      title={icon}
                    >
                      <span className="material-icons" style={{ fontSize: '20px', color: formData.icon === icon ? 'white' : '#374151' }}>
                        {icon}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center shadow-md"
                    style={{ backgroundColor: formData.color }}
                  >
                    <span className="material-icons text-white" style={{ fontSize: '28px' }}>
                      {formData.icon}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({...formData, icon: e.target.value})}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition-all shadow-sm"
                    placeholder="Ex: person, security, admin_panel_settings..."
                  />
                </div>
              </div>

              {/* Couleur */}
              <div>
                <label className="flex text-sm font-bold text-gray-800 mb-3 items-center gap-2">
                  <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  Couleur
                </label>
                <div className="grid grid-cols-5 gap-3 mb-3">
                  {colorOptions.map((colorOption) => (
                    <button
                      key={colorOption.value}
                      type="button"
                      onClick={() => setFormData({...formData, color: colorOption.value})}
                      className={`h-12 rounded-xl transition-all hover:scale-105 hover:shadow-lg flex items-center justify-center ${
                        formData.color === colorOption.value 
                          ? 'ring-4 ring-offset-2 ring-[#D4AF37] scale-105' 
                          : ''
                      }`}
                      style={{ backgroundColor: colorOption.value }}
                      title={colorOption.name}
                    >
                      {formData.color === colorOption.value && (
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  className="w-full h-12 rounded-xl border-2 border-gray-300 cursor-pointer"
                />
              </div>

              </div>
              
              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t-2 border-gray-200 flex-shrink-0 bg-white rounded-b-2xl">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition-all font-semibold"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#c09d2a] text-white rounded-xl hover:shadow-lg transition-all font-semibold flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {editingRole ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminRolesPage
